/**
 * Local-date helpers.
 *
 * IMPORTANT: never use `new Date().toISOString().slice(0,10)` for a form's "today"
 * date — toISOString() is in UTC, so in the evening for timezones behind UTC the
 * date jumps to TOMORROW. That pushed freshly-created forms out of the "current
 * week" list filter (which is computed in local time) and made them disappear.
 * Use these helpers so "today" is the user's LOCAL calendar day.
 */

// Local calendar day as 'YYYY-MM-DD' (no UTC shift).
export function localDateString(d = new Date()) {
    const x = d instanceof Date ? d : new Date(d);
    if (isNaN(x.getTime())) return '';
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, '0');
    const day = String(x.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

// Local date-time as 'YYYY-MM-DDTHH:mm' — for <input type="datetime-local"> values.
// Same reasoning as localDateString: avoid the UTC day/time shift.
export function localDateTimeString(d = new Date()) {
    const x = d instanceof Date ? d : new Date(d);
    if (isNaN(x.getTime())) return '';
    const hh = String(x.getHours()).padStart(2, '0');
    const mi = String(x.getMinutes()).padStart(2, '0');
    return `${localDateString(x)}T${hh}:${mi}`;
}

// Parse a date-only 'YYYY-MM-DD' string as a LOCAL date (no UTC shift).
// `new Date('2026-06-08')` parses as UTC midnight, which renders as the previous
// day in any timezone behind UTC. Returns null when not a plain 'YYYY-MM-DD'.
export function parseLocalDate(value) {
    if (typeof value !== 'string') return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// Format as 'MM/DD/YYYY'. Date-only 'YYYY-MM-DD' values are formatted straight
// from their parts (no Date object) so they never shift across timezones; full
// timestamps fall back to local-time formatting.
export function formatDateUS(value) {
    if (!value) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
    if (m) return `${m[2]}/${m[3]}/${m[1]}`;
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
}

// Locale-aware short date (Date.toLocaleDateString) that treats date-only
// 'YYYY-MM-DD' values as local dates to avoid the UTC off-by-one-day shift.
export function formatDateLocale(value, locales, options) {
    if (!value) return '';
    const d = parseLocalDate(value) || new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(locales, options);
}

export default localDateString;
